/**
 * ImageUpload — reusable image/logo upload for the v2 builder. Uploads to the
 * S3-backed media library and uses the returned hosted URL; falls back to an
 * inline data URL (small files only) if the upload isn't available. Also
 * accepts a pasted URL.
 */
import React, { useContext, useRef, useState } from 'react';
import { webPresenceApi } from '../../shared/services/platformApi';
import { AssetPicker } from './AssetPicker';
import { BrandCtx } from './brand';

const MAX_BYTES = 8_000_000; // 8MB — uploaded to S3.
const FALLBACK_MAX = 1_500_000; // inline data-URL fallback only for small files.

export function ImageUpload({ value, onChange, label = 'Image' }: { value?: string; onChange: (v: string | undefined) => void; label?: string }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const brand = useContext(BrandCtx);
  const pick = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file'); return; }
    if (file.size > MAX_BYTES) { setErr('Image is too large (max 8MB)'); return; }
    setErr(null);
    setBusy(true);
    const res = await webPresenceApi.uploadAsset(file); // hosted S3 URL
    if (res && res.url) { onChange(res.url); setBusy(false); return; }
    if (file.size > FALLBACK_MAX) { setErr('Upload failed and the file is too large to embed — try again or paste a hosted URL'); setBusy(false); return; }
    const reader = new FileReader();
    reader.onload = () => { onChange(typeof reader.result === 'string' ? reader.result : undefined); setBusy(false); };
    reader.readAsDataURL(file);
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
          style={{ width: 56, height: 56, borderRadius: 8, border: '1px dashed #c7b8f0', background: value ? `#fff url(${JSON.stringify(value)}) center/contain no-repeat` : '#faf9ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#7468ae', flexShrink: 0 }}
          title="Click or drop an image"
        >
          {!value && '⬆'}
        </div>
        <div style={{ display: 'grid', gap: 4, flex: 1 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1px solid #d7dbe0', background: '#fff', fontSize: 12.5, fontWeight: 600, cursor: busy ? 'default' : 'pointer', color: '#14171c' }}>{busy ? 'Uploading…' : 'Upload…'}</button>
            <button type="button" onClick={() => setPicking(true)} style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: '#5b4b9e' }}>Library</button>
          </div>
          {brand.logoSrc && brand.logoSrc !== value && (
            <button type="button" onClick={() => onChange(brand.logoSrc)} style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', color: '#5b4b9e' }}>Use brand logo</button>
          )}
          {value && <button type="button" onClick={() => onChange(undefined)} style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid #e6e8ec', background: '#fff', fontSize: 11.5, cursor: 'pointer', color: '#dc2626' }}>Remove</button>}
        </div>
      </div>
      {picking && <AssetPicker onPick={(url) => onChange(url)} onClose={() => setPicking(false)} />}
      <input
        style={{ display: 'block', width: '100%', marginTop: 2, padding: '6px 8px', border: '1px solid #d7dbe0', borderRadius: 6, fontSize: 12.5 }}
        type="text" value={value && value.startsWith('data:') ? '' : (value || '')} placeholder="…or paste an image URL"
        onChange={(e) => onChange(e.target.value || undefined)}
      />
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => pick(e.target.files?.[0])} />
      {err && <span style={{ fontSize: 11, color: '#dc2626' }}>{err}</span>}
    </div>
  );
}
